import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
 breadcrumbListSchema,
 collectionPageSchema,
 faqPageSchema,
 jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { conditionFromSlug, SEO_CONDITIONS } from "@/lib/seo-conditions";
import { SEO_CITIES, citySlug, specialtySlug } from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/conditions/$condition")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const cond = conditionFromSlug(params.condition);
 if (!cond) throw notFound();
 return { cond };
 },
 head: ({ params, loaderData }) => {
 if (!loaderData) return {};
 const { cond } = loaderData;
 const title = `${cond.name}, Find Care & Treatment | Heallexa`;
 const description = `Learn about ${cond.name.toLowerCase()} and find providers across the U.S. who can help. ${cond.intro}`;
 const url = `${SITE}/conditions/${params.condition}`;
 const decision = decideIndexability({
 providerCount: 1,
 hasFaq: true,
 hasRelatedProviders: true,
 hasNearbyCities: true,
 internalLinkCount: 30,
 });
 return {
 meta: [
 { title },
 { name: "description", content: description },
 robotsMeta(decision.robots),
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "article" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 jsonLdScript(collectionPageSchema({ name: title, description, url })),
 jsonLdScript(
 breadcrumbListSchema([
 { name: "Home", url: "/" },
 { name: "Conditions", url: "/conditions" },
 { name: cond.name, url: `/conditions/${cond.slug}` },
 ]),
 ),
 jsonLdScript(
 faqPageSchema([
 { q: `Who treats ${cond.name.toLowerCase()}?`, a: `${cond.specialties.join(", ")} commonly help patients with ${cond.name.toLowerCase()}.` },
 { q: `Do I need a referral?`, a: `Most plans allow self-referral for these specialties; check with your insurance to be sure.` },
 { q: `Is this medical advice?`, a: `No. Heallexa is a directory. Always speak with a qualified clinician about your specific situation.` },
 ]),
 ),
 ],
 };
 },
 component: ConditionPage,
});

function ConditionPage() {
 const { cond } = Route.useLoaderData();

 const specialtyLinks = cond.specialties.map((s: string) => ({
 label: `Find a ${s.toLowerCase().replace(/s$/, "")}`,
 href: `/specialties/${specialtySlug(s)}`,
 }));

 const cityLinks = SEO_CITIES.slice(0, 24).map((c) => ({
 label: `${cond.name} care in ${c}`,
 href: `/conditions/${cond.slug}/${citySlug(c)}`,
 }));

 const related = (cond.relatedConditions ?? [])
 .map((slug: string) => SEO_CONDITIONS.find((c) => c.slug === slug))
 .filter(Boolean)
 .map((c: any) => ({ label: c!.name, href: `/conditions/${c!.slug}` }));

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-5xl px-6 py-12">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Home", href: "/" },
 { name: "Conditions", href: "/conditions" },
 { name: cond.name },
 ]}
 />
 <header className="mb-8">
 <p className="text-sm font-medium text-primary mb-2">Condition overview</p>
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
 {cond.name}
 </h1>
 <p className="mt-4 text-muted-foreground max-w-2xl">{cond.intro}</p>
 </header>

 <section className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
 Educational only, not medical advice. Always consult a qualified healthcare provider.
 </section>

 <SeoLinkList heading="Specialists who treat this" links={specialtyLinks} columns={3} className="mb-8" />
 <SeoLinkList heading={`${cond.name} care by city`} links={cityLinks} columns={3} className="mb-8" />
 {related.length > 0 && (
 <SeoLinkList heading="Related conditions" links={related} columns={3} className="mb-8" />
 )}

 <SeoFaq
 className="mb-8"
 items={[
 { q: `When should I see a provider for ${cond.name.toLowerCase()}?`, a: `If symptoms persist, worsen, or interfere with daily life, schedule an evaluation with one of the specialties above.` },
 { q: `Is care for ${cond.name.toLowerCase()} usually covered by insurance?`, a: `Most major insurance plans cover evaluation and treatment. Verify benefits with your carrier and the provider.` },
 { q: `Can I book through Heallexa?`, a: `Yes, pick a provider and book online if they support it, otherwise call the office directly.` },
 ]}
 />

 <div className="text-sm text-muted-foreground">
 <Link to="/" className="text-primary hover:underline">Back to Heallexa</Link>
 </div>
 </main>
 <FooterSection />
 </div>
 );
}
